/**
 * Use this file for JavaScript code that you want to run in the front-end 
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any 
 * JavaScript running in the front-end, then you should delete this file and remove 
 * the `viewScript` property from `block.json`. 
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

/* eslint-disable no-console */
// console.log("Hello World! (from ultime-blocks-color-picker block)");
/* eslint-enable no-console */

(function () {
    'use strict';

    let R = 0, G = 68, B = 114;
    let hist = [];
    let toastTimer = null;
    const MAX_COLORS = 16;
    const STORAGE_KEY = 'cpv7';

    const ge = (id) => document.getElementById(id);
    const h2 = (n) => n.toString(16).padStart(2, '0');
    const rd = (n, d = 2) => parseFloat(n.toFixed(d));
    const norm = (v) => rd(v / 255, 4); // normalize 0-255 → 0.0-1.0 for GLSL

    /* ── Color conversions ── */
    const toHsl = (r, g, b) => {
        r /= 255; g /= 255; b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        let h = 0, s = 0;
        const l = (max + min) / 2;
        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
            else if (max === g) h = ((b - r) / d + 2) / 6;
            else h = ((r - g) / d + 4) / 6;
        }
        return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
    };

    const toCmyk = (r, g, b) => {
        r /= 255; g /= 255; b /= 255;
        const k = 1 - Math.max(r, g, b);
        if (k === 1) return [0, 0, 0, 100];
        return [
            Math.round((1 - r - k) / (1 - k) * 100),
            Math.round((1 - g - k) / (1 - k) * 100),
            Math.round((1 - b - k) / (1 - k) * 100),
            Math.round(k * 100)
        ];
    };

    const toHsb = (r, g, b) => {
        r /= 255; g /= 255; b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        const d = max - min;
        let h = 0;
        const s = max === 0 ? 0 : d / max;
        if (d !== 0) {
            if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
            else if (max === g) h = ((b - r) / d + 2) / 6;
            else h = ((r - g) / d + 4) / 6;
        }
        return [Math.round(h * 360), Math.round(s * 100), Math.round(max * 100)];
    };

    const toHwb = (r, g, b) => {
        r /= 255; g /= 255; b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        const d = max - min;
        let h = 0;
        if (d !== 0) {
            if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
            else if (max === g) h = ((b - r) / d + 2) / 6;
            else h = ((r - g) / d + 4) / 6;
        }
        return [Math.round(h * 360), Math.round(min * 100), Math.round((1 - max) * 100)];
    };

    const toXyz = (r, g, b) => {
        const lin = (c) => {
            c /= 255;
            return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        };
        const rl = lin(r), gl = lin(g), bl = lin(b);
        return [
            rd(rl * 0.4124564 + gl * 0.3575761 + bl * 0.1804375, 4),
            rd(rl * 0.2126729 + gl * 0.7151522 + bl * 0.0721750, 4),
            rd(rl * 0.0193339 + gl * 0.1191920 + bl * 0.9503041, 4)
        ];
    };

    const toLab = (r, g, b) => {
        const [x, y, z] = toXyz(r, g, b);
        const f = (t) => t > 0.008856 ? Math.pow(t, 1 / 3) : 7.787 * t + 16 / 116;
        const fx = f(x / 0.95047), fy = f(y), fz = f(z / 1.08883);
        return [rd(116 * fy - 16), rd(500 * (fx - fy)), rd(200 * (fy - fz))];
    };

    const toLch = (r, g, b) => {
        const [L, a, bv] = toLab(r, g, b);
        const c = Math.sqrt(a * a + bv * bv);
        let h = Math.atan2(bv, a) * 180 / Math.PI;
        if (h < 0) h += 360;
        return [L, rd(c), rd(h, 1)];
    };

    const toOklch = (r, g, b) => {
        const lin = (c) => {
            c /= 255;
            return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        };
        const rl = lin(r), gl = lin(g), bl = lin(b);
        const lo = Math.pow(0.4122214708 * rl + 0.5363325363 * gl + 0.0514459929 * bl, 1 / 3);
        const mo = Math.pow(0.2119034982 * rl + 0.6806995451 * gl + 0.1073969566 * bl, 1 / 3);
        const so = Math.pow(0.0883024619 * rl + 0.2817188376 * gl + 0.6299787005 * bl, 1 / 3);
        const L = 0.2104542553 * lo + 0.7936177850 * mo - 0.0040720468 * so;
        const a = 1.9779984951 * lo - 2.4285922050 * mo + 0.4505937099 * so;
        const bk = 0.0259040371 * lo + 0.7827717662 * mo - 0.8086757660 * so;
        const c = Math.sqrt(a * a + bk * bk);
        let h = Math.atan2(bk, a) * 180 / Math.PI;
        if (h < 0) h += 360;
        return [rd(L, 4), rd(c, 4), rd(h, 1)];
    };

    const lum = (r, g, b) => {
        const f = (c) => {
            c /= 255;
            return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        };
        return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
    };

    /* ── Toast ── */
    const toast = (msg) => {
        const t = ge('cp-toast');
        t.textContent = msg;
        t.classList.add('on');
        clearTimeout(toastTimer);
        toastTimer = setTimeout(() => t.classList.remove('on'), 2400);
    };

    /* ── Download ── */
    const dl = (blob, name) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    /* ── Canvas helper : flat color + labels ── */
    const makeCanvas = (r, g, b, w = 1920, h = 1080) => {
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d');
        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = lum(r, g, b) > 0.179 ? 'rgba(0,0,0,.6)' : 'rgba(255,255,255,.9)';
        ctx.font = 'bold 56px system-ui'; ctx.textAlign = 'center';
        ctx.fillText(ge('cp-v-hex').textContent, w / 2, h * 0.426);
        ctx.font = '30px system-ui';
        ctx.fillText(ge('cp-v-rgb').textContent, w / 2, h * 0.478);
        ctx.fillText(ge('cp-v-hsl').textContent, w / 2, h * 0.517);
        ctx.fillText(ge('cp-v-hsb').textContent, w / 2, h * 0.556);
        return c;
    };

    /* ── Canvas helper : palette swatches ── */
    const makePalCanvas = (W = 160, H = 180) => {
        const c = document.createElement('canvas');
        c.width = W * hist.length; c.height = H;
        const ctx = c.getContext('2d');
        hist.forEach((e, i) => {
            ctx.fillStyle = e.hex;
            ctx.fillRect(i * W, 0, W, H);
            ctx.fillStyle = lum(e.r, e.g, e.b) > 0.179 ? 'rgba(0,0,0,.7)' : 'rgba(255,255,255,.9)';
            ctx.font = 'bold 13px system-ui'; ctx.textAlign = 'center';
            ctx.fillText(e.hex, i * W + W / 2, H - 24);
        });
        return c;
    };

    /* ── Copy to clipboard ── */
    const copyText = (text, label) => {
        navigator.clipboard.writeText(text).then(() => toast(label + ' copied'));
    };

    /* ── Update UI ── */
    const upd = (r, g, b) => {
        R = r; G = g; B = b;
        const hex = '#' + h2(r) + h2(g) + h2(b);
        const hexU = hex.toUpperCase();
        const hsl = toHsl(r, g, b);
        const cmy = toCmyk(r, g, b);
        const hsb = toHsb(r, g, b);
        const hwb = toHwb(r, g, b);
        const lab = toLab(r, g, b);
        const lch = toLch(r, g, b);
        const okl = toOklch(r, g, b);
        const xyz = toXyz(r, g, b);

        ge('cp-preview').style.background = hex;
        ge('cp-native').value = hex;
        ge('cp-hex-in').value = hexU;
        ge('cp-sr').value = r; ge('cp-rv').textContent = r;
        ge('cp-sg').value = g; ge('cp-gv').textContent = g;
        ge('cp-sb').value = b; ge('cp-bv').textContent = b;

        ge('cp-v-hex').textContent = hexU;
        ge('cp-v-rgb').textContent = `rgb(${r}, ${g}, ${b})`;
        ge('cp-v-hsl').textContent = `hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`;
        ge('cp-v-cmy').textContent = `cmyk(${cmy[0]}%, ${cmy[1]}%, ${cmy[2]}%, ${cmy[3]}%)`;
        ge('cp-v-hsb').textContent = `hsb(${hsb[0]}, ${hsb[1]}%, ${hsb[2]}%)`;
        ge('cp-v-hwb').textContent = `hwb(${hwb[0]} ${hwb[1]}% ${hwb[2]}%)`;
        ge('cp-v-lab').textContent = `lab(${lab[0]} ${lab[1]} ${lab[2]})`;
        ge('cp-v-lch').textContent = `lch(${lch[0]} ${lch[1]} ${lch[2]})`;
        ge('cp-v-okl').textContent = `oklch(${okl[0]} ${okl[1]} ${okl[2]})`;
        ge('cp-v-xyz').textContent = `xyz(${xyz[0]} ${xyz[1]} ${xyz[2]})`;
    };

    /* ── Palette ── */
    const saveColor = () => {
        const hx = ('#' + h2(R) + h2(G) + h2(B)).toUpperCase();
        if (hist.some(e => e.hex === hx)) { toast('Color already in palette'); return; }
        if (hist.length >= MAX_COLORS) hist.shift();
        hist.push({ hex: hx, r: R, g: G, b: B });
        persistHist();
        renderPal();
        toast('Saved: ' + hx);
    };

    const persistHist = () => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(hist)); } catch (_) { }
    };

    const loadHist = () => {
        try {
            const s = localStorage.getItem(STORAGE_KEY);
            if (s) { hist = JSON.parse(s); renderPal(); }
        } catch (_) { }
    };

    const renderPal = () => {
        const pg = ge('cp-pg');
        const emp = ge('cp-emp');
        pg.querySelectorAll('.cp-sw').forEach(sw => pg.removeChild(sw));
        if (!hist.length) { emp.style.display = 'inline'; return; }
        emp.style.display = 'none';
        hist.forEach((e, idx) => {
            const sw = document.createElement('div');
            sw.className = 'cp-sw';
            sw.style.background = e.hex;
            sw.title = e.hex;
            const x = document.createElement('span');
            x.className = 'cp-swx';
            x.textContent = '×';
            x.addEventListener('click', (ev) => {
                ev.stopPropagation();
                hist.splice(idx, 1);
                persistHist();
                renderPal();
            });
            sw.appendChild(x);
            sw.addEventListener('click', () => upd(e.r, e.g, e.b));
            pg.appendChild(sw);
        });
    };

    /* ── EyeDropper ── */
    const pipette = () => {
        if (!window.EyeDropper) return;
        const ed = new EyeDropper();
        const btn = ge('cp-pip');
        btn.textContent = 'Picking…';
        btn.disabled = true;
        ed.open().then((res) => {
            const hx = res.sRGBHex;
            upd(parseInt(hx.slice(1, 3), 16), parseInt(hx.slice(3, 5), 16), parseInt(hx.slice(5, 7), 16));
            toast('Picked: ' + hx.toUpperCase());
        }).catch(() => {
            toast('Cancelled');
        }).finally(() => {
            btn.innerHTML = pipetteInner;
            btn.disabled = false;
        });
    };

    /* ── Exports — current color ── */
    const expJpeg = () => {
        makeCanvas(R, G, B).toBlob((blob) => dl(blob, `color-${R}-${G}-${B}.jpg`), 'image/jpeg', 0.92);
        toast('Downloading JPEG…');
    };

    const expPng = () => {
        makeCanvas(R, G, B).toBlob((blob) => dl(blob, `color-${R}-${G}-${B}.png`), 'image/png');
        toast('Downloading PNG…');
    };

    const expWebp = () => {
        makeCanvas(R, G, B).toBlob((blob) => dl(blob, `color-${R}-${G}-${B}.webp`), 'image/webp', 0.92);
        toast('Downloading WebP…');
    };

    const expSvg = () => {
        const hx = ge('cp-v-hex').textContent;
        const tc = lum(R, G, B) > 0.179 ? '#000' : '#fff';
        const s = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="220">`
            + `<rect width="400" height="220" fill="${hx}"/>`
            + `<text x="200" y="90" font-family="system-ui" font-size="38" font-weight="bold" fill="${tc}" text-anchor="middle">${hx}</text>`
            + `<text x="200" y="130" font-family="system-ui" font-size="16" fill="${tc}" text-anchor="middle">${ge('cp-v-rgb').textContent}</text>`
            + `<text x="200" y="158" font-family="system-ui" font-size="16" fill="${tc}" text-anchor="middle">${ge('cp-v-hsl').textContent}</text>`
            + `</svg>`;
        dl(new Blob([s], { type: 'image/svg+xml' }), `color-${hx.slice(1)}.svg`);
        toast('SVG downloaded');
    };

    const expCss = () => {
        const hx = ge('cp-v-hex').textContent;
        const hsl = toHsl(R, G, B);
        const okl = toOklch(R, G, B);
        const css = `:root {\n`
            + `  --color: ${hx};\n`
            + `  --color-rgb: ${R}, ${G}, ${B};\n`
            + `  --color-hsl: ${hsl[0]}deg ${hsl[1]}% ${hsl[2]}%;\n`
            + `  --color-oklch: ${okl[0]} ${okl[1]} ${okl[2]};\n`
            + `}`;
        dl(new Blob([css], { type: 'text/css' }), `color-${hx.slice(1)}.css`);
        toast('CSS downloaded');
    };

    const expJson = () => {
        const hsl = toHsl(R, G, B), cmy = toCmyk(R, G, B);
        const hsb = toHsb(R, G, B), hwb = toHwb(R, G, B);
        const lab = toLab(R, G, B), lch = toLch(R, G, B);
        const okl = toOklch(R, G, B), xyz = toXyz(R, G, B);
        const obj = {
            hex: ge('cp-v-hex').textContent,
            rgb: { r: R, g: G, b: B },
            hsl: { h: hsl[0], s: hsl[1], l: hsl[2] },
            cmyk: { c: cmy[0], m: cmy[1], y: cmy[2], k: cmy[3] },
            hsb: { h: hsb[0], s: hsb[1], b: hsb[2] },
            hwb: { h: hwb[0], w: hwb[1], b: hwb[2] },
            lab: { l: lab[0], a: lab[1], b: lab[2] },
            lch: { l: lch[0], c: lch[1], h: lch[2] },
            oklch: { l: okl[0], c: okl[1], h: okl[2] },
            xyz: { x: xyz[0], y: xyz[1], z: xyz[2] }
        };
        dl(new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' }), 'color.json');
        toast('JSON downloaded');
    };

    const expGlsl = () => {
        const hx = ge('cp-v-hex').textContent;
        const hsl = toHsl(R, G, B);
        const okl = toOklch(R, G, B);
        const rn = norm(R), gn = norm(G), bn = norm(B);
        const glsl = `// Color: ${hx}\n`
            + `// Source: Color Picker\n\n`
            + `// vec3 — linear RGB (0.0 to 1.0)\n`
            + `vec3 color = vec3(${rn}, ${gn}, ${bn});\n\n`
            + `// vec4 — with alpha\n`
            + `vec4 colorAlpha = vec4(${rn}, ${gn}, ${bn}, 1.0);\n\n`
            + `// uniform declaration\n`
            + `uniform vec3 u_color; // ${hx} → vec3(${rn}, ${gn}, ${bn})\n\n`
            + `// HSL reference: hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)\n`
            + `// OKLCH reference: oklch(${okl[0]}, ${okl[1]}, ${okl[2]})\n`;
        dl(new Blob([glsl], { type: 'text/plain' }), `color-${hx.slice(1)}.glsl`);
        toast('GLSL downloaded');
    };

    /* ── Exports — full palette ── */
    const expPalJpeg = () => {
        if (!hist.length) { toast('Palette is empty'); return; }
        makePalCanvas().toBlob((blob) => dl(blob, 'palette.jpg'), 'image/jpeg', 0.92);
        toast('Palette JPEG downloaded');
    };

    const expPalPng = () => {
        if (!hist.length) { toast('Palette is empty'); return; }
        makePalCanvas().toBlob((blob) => dl(blob, 'palette.png'), 'image/png');
        toast('Palette PNG downloaded');
    };

    const expPalWebp = () => {
        if (!hist.length) { toast('Palette is empty'); return; }
        makePalCanvas().toBlob((blob) => dl(blob, 'palette.webp'), 'image/webp', 0.92);
        toast('Palette WebP downloaded');
    };

    const expPalSvg = () => {
        if (!hist.length) { toast('Palette is empty'); return; }
        const W = 120, H = 140;
        const rects = hist.map((e, i) => {
            const tc = lum(e.r, e.g, e.b) > 0.179 ? '#000' : '#fff';
            return `<rect x="${i * W}" y="0" width="${W}" height="${H}" fill="${e.hex}"/>`
                + `<text x="${i * W + W / 2}" y="${H - 16}" font-family="system-ui" font-size="11" fill="${tc}" text-anchor="middle">${e.hex}</text>`;
        }).join('');
        dl(new Blob([
            `<svg xmlns="http://www.w3.org/2000/svg" width="${W * hist.length}" height="${H}">${rects}</svg>`
        ], { type: 'image/svg+xml' }), 'palette.svg');
        toast('Palette SVG downloaded');
    };

    const expPalCss = () => {
        if (!hist.length) { toast('Palette is empty'); return; }
        const lines = hist.map((e, i) => `  --palette-${i + 1}: ${e.hex};`).join('\n');
        dl(new Blob([`:root {\n${lines}\n}`], { type: 'text/css' }), 'palette.css');
        toast('Palette CSS downloaded');
    };

    const expPalJson = () => {
        if (!hist.length) { toast('Palette is empty'); return; }
        const data = hist.map(e => {
            const hsl = toHsl(e.r, e.g, e.b);
            const okl = toOklch(e.r, e.g, e.b);
            return { hex: e.hex, rgb: { r: e.r, g: e.g, b: e.b }, hsl: { h: hsl[0], s: hsl[1], l: hsl[2] }, oklch: { l: okl[0], c: okl[1], h: okl[2] } };
        });
        dl(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }), 'palette.json');
        toast('Palette JSON downloaded');
    };

    const expPalGlsl = () => {
        if (!hist.length) { toast('Palette is empty'); return; }
        const lines = hist.map((e, i) =>
            `vec3 palette_${i + 1} = vec3(${norm(e.r)}, ${norm(e.g)}, ${norm(e.b)}); // ${e.hex}`
        ).join('\n');
        const glsl = `// Palette — ${hist.length} color(s)\n// Source: Color Picker\n\n${lines}\n\n`
            + `// As array:\n`
            + `vec3 palette[${hist.length}] = vec3[](\n`
            + hist.map((e, i) => `  vec3(${norm(e.r)}, ${norm(e.g)}, ${norm(e.b)})${i < hist.length - 1 ? ',' : ''} // ${e.hex}`).join('\n')
            + `\n);`;
        dl(new Blob([glsl], { type: 'text/plain' }), 'palette.glsl');
        toast('Palette GLSL downloaded');
    };

    /* ── Pipette button inner HTML ── */
    const pipetteInner = ge('cp-pip').innerHTML;

    /* ── Event listeners ── */
    ge('cp-pip').addEventListener('click', pipette);
    ge('cp-save').addEventListener('click', saveColor);
    ge('cp-cpy').addEventListener('click', () => copyText(ge('cp-v-hex').textContent, 'HEX'));
    ge('cp-rst').addEventListener('click', () => { upd(0, 68, 114); toast('Reset'); });
    ge('cp-clr').addEventListener('click', () => { hist = []; persistHist(); renderPal(); toast('Palette cleared'); });

    ge('cp-native').addEventListener('input', function () {
        const hx = this.value;
        upd(parseInt(hx.slice(1, 3), 16), parseInt(hx.slice(3, 5), 16), parseInt(hx.slice(5, 7), 16));
    });
    ge('cp-hex-in').addEventListener('input', function () {
        let v = this.value.trim();
        if (v.charAt(0) !== '#') v = '#' + v;
        if (/^#[0-9a-fA-F]{6}$/.test(v))
            upd(parseInt(v.slice(1, 3), 16), parseInt(v.slice(3, 5), 16), parseInt(v.slice(5, 7), 16));
    });
    ge('cp-hex-in').addEventListener('blur', function () {
        if (!/^#[0-9a-fA-F]{6}$/i.test(this.value))
            this.value = ('#' + h2(R) + h2(G) + h2(B)).toUpperCase();
    });
    ge('cp-hex-in').addEventListener('keydown', (e) => { if (e.key === 'Enter') ge('cp-hex-in').blur(); });

    ge('cp-sr').addEventListener('input', function () { upd(parseInt(this.value), G, B); });
    ge('cp-sg').addEventListener('input', function () { upd(R, parseInt(this.value), B); });
    ge('cp-sb').addEventListener('input', function () { upd(R, G, parseInt(this.value)); });

    const valueCopies = [
        ['cp-bx-hex', 'cp-v-hex', 'HEX'],
        ['cp-bx-rgb', 'cp-v-rgb', 'RGB'],
        ['cp-bx-hsl', 'cp-v-hsl', 'HSL'],
        ['cp-bx-cmy', 'cp-v-cmy', 'CMYK'],
        ['cp-bx-hsb', 'cp-v-hsb', 'HSB'],
        ['cp-bx-hwb', 'cp-v-hwb', 'HWB'],
        ['cp-bx-lab', 'cp-v-lab', 'LAB'],
        ['cp-bx-lch', 'cp-v-lch', 'LCH'],
        ['cp-bx-okl', 'cp-v-okl', 'OKLCH'],
        ['cp-bx-xyz', 'cp-v-xyz', 'XYZ'],
    ];
    valueCopies.forEach(([boxId, valId, label]) => {
        ge(boxId).addEventListener('click', () => copyText(ge(valId).textContent, label));
    });

    ge('cp-ej').addEventListener('click', expJpeg);
    ge('cp-epng').addEventListener('click', expPng);
    ge('cp-ew').addEventListener('click', expWebp);
    ge('cp-es').addEventListener('click', expSvg);
    ge('cp-ec').addEventListener('click', expCss);
    ge('cp-en').addEventListener('click', expJson);
    ge('cp-eg').addEventListener('click', expGlsl);
    ge('cp-epj').addEventListener('click', expPalJpeg);
    ge('cp-eppng').addEventListener('click', expPalPng);
    ge('cp-epwp').addEventListener('click', expPalWebp);
    ge('cp-eps').addEventListener('click', expPalSvg);
    ge('cp-epc').addEventListener('click', expPalCss);
    ge('cp-epn').addEventListener('click', expPalJson);
    ge('cp-epg').addEventListener('click', expPalGlsl);

    if (!window.EyeDropper) {
        ge('cp-pip').disabled = true;
        ge('cp-no-ed').style.display = 'inline-block';
    }

    loadHist();
    upd(0, 68, 114);

})();